import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { jwtDecode } from "jwt-decode";

import {
  useGetAllUsers,
  useGetAllTasks,
  useGetTasksByUser,
  useGetTasksByDepartment,
  useGetTasksByDate,
  useAddTask,
  useEditTask,
  useDeleteTask,
} from "../../../api/addEmployee/employee";

import TaskHeader from "./TaskHeader";
import TaskFilters from "./TaskFilters";
import TaskList from "./TaskList";
import TaskLegend from "./TaskLegend";
import TaskModel from "./TaskModel";
import TaskCalendar from "./TaskCalendar";

import { notifySuccess, notifyError } from "../../../utils/ToastifyProvider";

const AdminTask = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentUserDepartment, setCurrentUserDepartment] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [viewMode, setViewMode] = useState("all");
  const [departmentFilterDate, setDepartmentFilterDate] = useState("");
  const [editingTask, setEditingTask] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);

  // Pagination states
  const [userTasksPage, setUserTasksPage] = useState(1);
  const [departmentTasksPage, setDepartmentTasksPage] = useState(1);
  const [dateTasksPage, setDateTasksPage] = useState(1);
  const [limit] = useState(20);

  // Accumulated tasks for infinite scroll
  const [accumulatedUserTasks, setAccumulatedUserTasks] = useState([]);
  const [accumulatedDepartmentTasks, setAccumulatedDepartmentTasks] = useState([]);
  const [accumulatedDateTasks, setAccumulatedDateTasks] = useState([]);

  // Decode token and get user info
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setCurrentUserId(decoded.userId || decoded.id);
        setCurrentUserRole(decoded.role);
        setCurrentUserDepartment(decoded.department);
        
        // If manager, auto-select their department
        if (decoded.role === "manager" && decoded.department) {
          setSelectedDepartment(decoded.department);
          setViewMode("department");
        }
      } catch (error) {
        console.error("Invalid token", error);
      }
    }
  }, []);

  // API hooks - Filter users by department for managers
  const { data: allUsers = [], isLoading: usersLoading } = useGetAllUsers(searchQuery);
  
  // Filter users based on role
  const users = currentUserRole === "manager" 
    ? allUsers.filter(user => user.department === currentUserDepartment)
    : allUsers;

  const { data: userTasksData, isLoading: userTasksLoading } =
    useGetTasksByUser({
      userId: selectedEmployee?._id,
      page: userTasksPage,
      limit,
      enabled: !!selectedEmployee,
    });

  const { data: departmentTasksData, isLoading: departmentTasksLoading } =
    useGetTasksByDepartment({
      department: selectedDepartment,
      page: departmentTasksPage,
      limit,
      enabled: !!selectedDepartment,
    });

  const { data: dateTasksData, isLoading: dateTasksLoading } =
    useGetTasksByDate({
      date: selectedDate,
      page: dateTasksPage,
      limit,
      enabled: !!selectedDate && viewMode === "all",
      // For managers, only fetch tasks from their department
      department: currentUserRole === "manager" ? currentUserDepartment : undefined,
    });

  // Extract tasks and pagination info
  const userTasks = userTasksData?.tasks || [];
  const userTasksTotalPages = userTasksData?.totalPages || 1;

  const departmentTasks = departmentTasksData?.tasks || [];
  const departmentTasksTotalPages = departmentTasksData?.totalPages || 1;

  const dateTasks = dateTasksData?.tasks || [];
  const dateTasksTotalPages = dateTasksData?.totalPages || 1;

  // Accumulate user tasks
  useEffect(() => {
    if (userTasks.length > 0) {
      setAccumulatedUserTasks((prev) => {
        if (userTasksPage === 1) return userTasks;
        const existingIds = new Set(prev.map((t) => t._id));
        const newTasks = userTasks.filter((t) => !existingIds.has(t._id));
        return [...prev, ...newTasks];
      });
    }
  }, [userTasks, userTasksPage]);

  // Accumulate department tasks
  useEffect(() => {
    if (departmentTasks.length > 0) {
      setAccumulatedDepartmentTasks((prev) => {
        if (departmentTasksPage === 1) return departmentTasks;
        const existingIds = new Set(prev.map((t) => t._id));
        const newTasks = departmentTasks.filter((t) => !existingIds.has(t._id));
        return [...prev, ...newTasks];
      });
    }
  }, [departmentTasks, departmentTasksPage]);

  // Accumulate date tasks
  useEffect(() => {
    if (dateTasks.length > 0) {
      setAccumulatedDateTasks((prev) => {
        if (dateTasksPage === 1) return dateTasks;
        const existingIds = new Set(prev.map((t) => t._id));
        const newTasks = dateTasks.filter((t) => !existingIds.has(t._id));
        return [...prev, ...newTasks];
      });
    }
  }, [dateTasks, dateTasksPage]);

  const addTaskMutation = useAddTask();
  const editTaskMutation = useEditTask();
  const deleteTaskMutation = useDeleteTask();

  const previousMonth = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  };

  const nextMonth = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
  };

  const handleDateClick = (dateKey) => {
    setSelectedCalendarDate(dateKey);
  };

  const resetModal = () => {
    setTaskTitle("");
    setTaskDescription("");
    setShowModal(false);
    setIsEditMode(false);
    setEditingTask(null);
  };

  const handleDepartmentSelect = (dept) => {
    // Managers can only select their own department
    if (currentUserRole === "manager" && dept !== currentUserDepartment) {
      notifyError("You can only view tasks from your department");
      return;
    }
    
    setViewMode("department");
    setSelectedDepartment(dept);
    setSelectedEmployee(null);
    setSearchQuery("");
    setSelectedDate("");
    setDepartmentTasksPage(1);
    setAccumulatedDepartmentTasks([]);
  };

  const handleEmployeeSelect = (employee) => {
    // Managers can only select employees from their department
    if (currentUserRole === "manager" && employee.department !== currentUserDepartment) {
      notifyError("You can only view tasks from employees in your department");
      return;
    }
    
    setViewMode("employee");
    setSelectedEmployee(employee);
    setSelectedDepartment("");
    setDepartmentFilterDate("");
    setSearchQuery("");
    setSelectedDate("");
    setUserTasksPage(1);
    setAccumulatedUserTasks([]);
  };

  const handleClearSelection = () => {
    setViewMode("all");
    setSelectedEmployee(null);
    // Managers should keep their department selected
    if (currentUserRole === "manager") {
      setSelectedDepartment(currentUserDepartment);
    } else {
      setSelectedDepartment("");
    }
    setDepartmentFilterDate("");
    setSelectedDate("");
    setSearchQuery("");
    setAccumulatedUserTasks([]);
    setAccumulatedDepartmentTasks([]);
    setAccumulatedDateTasks([]);
  };

  const handleTaskEdit = (task) => {
    setIsEditMode(true);
    setEditingTask(task);
    setTaskTitle(task.title);
    setTaskDescription(task.description);
    setSelectedDate(task.date.split("T")[0]);
    setShowModal(true);
  };

  const handleDeleteTask = (taskId) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      deleteTaskMutation.mutate(taskId);
    }
  };

  const handleTaskSubmit = async () => {
    if (!taskTitle.trim() || !taskDescription.trim()) {
      return;
    }
    if (!selectedDate) {
      return;
    }

    try {
      const taskData = {
        title: taskTitle,
        description: taskDescription,
        date: selectedDate,
      };

      if (isEditMode && editingTask) {
        await editTaskMutation.mutateAsync({
          taskId: editingTask._id,
          userId: selectedEmployee ? selectedEmployee._id : currentUserId,
          ...taskData,
        });
      } else {
        if (selectedEmployee) {
          taskData.userId = selectedEmployee._id;
        }
        await addTaskMutation.mutateAsync(taskData);
      }
      resetModal();
    } catch (error) {
      notifyError("Failed to save task. Please try again.");
    }
  };

  // Get tasks to display
  const getTasksToDisplay = () => {
    if (viewMode === "employee" && selectedEmployee) {
      return {
        tasks: accumulatedUserTasks,
        title: `Tasks for ${selectedEmployee.firstName} ${selectedEmployee.lastName}`,
        isLoading: userTasksLoading,
        currentPage: userTasksPage,
        totalPages: userTasksTotalPages,
        onPageChange: setUserTasksPage,
      };
    }
    if (viewMode === "department" && selectedDepartment) {
      let tasks = accumulatedDepartmentTasks;
      if (departmentFilterDate) {
        tasks = tasks.filter((task) => {
          const taskDate = new Date(task.date).toISOString().split("T")[0];
          return taskDate === departmentFilterDate;
        });
        return {
          tasks,
          title: `Tasks for ${selectedDepartment} Department on ${new Date(
            departmentFilterDate
          ).toLocaleDateString()}`,
          isLoading: departmentTasksLoading,
          currentPage: departmentTasksPage,
          totalPages: departmentTasksTotalPages,
          onPageChange: setDepartmentTasksPage,
        };
      }
      return {
        tasks,
        title: `Tasks for ${selectedDepartment} Department`,
        isLoading: departmentTasksLoading,
        currentPage: departmentTasksPage,
        totalPages: departmentTasksTotalPages,
        onPageChange: setDepartmentTasksPage,
      };
    }
    if (viewMode === "all" && selectedDate) {
      return {
        tasks: accumulatedDateTasks,
        title: `All Tasks on ${new Date(selectedDate).toLocaleDateString()}`,
        isLoading: dateTasksLoading,
        currentPage: dateTasksPage,
        totalPages: dateTasksTotalPages,
        onPageChange: setDateTasksPage,
      };
    }
    return { tasks: [], title: "Tasks", isLoading: false };
  };

  // Get available departments for filters
  const availableDepartments = currentUserRole === "manager" 
    ? [currentUserDepartment]
    : [...new Set(users.map((u) => u.department))].filter(Boolean);

  return (
    <div className="max-h-screen overflow-y-auto p-4 md:p-6">
      <div className="w-full max-w-[full] mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <TaskHeader
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
          previousMonth={previousMonth}
          nextMonth={nextMonth}
        />

        <TaskFilters
          role={currentUserRole || "admin"}
          users={users}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          departments={availableDepartments}
          selectedDepartment={selectedDepartment}
          setSelectedDepartment={setSelectedDepartment}
          selectedEmployee={selectedEmployee}
          setSelectedEmployee={setSelectedEmployee}
          setViewMode={setViewMode}
          departmentFilterDate={departmentFilterDate}
          setDepartmentFilterDate={setDepartmentFilterDate}
          handleDepartmentSelect={handleDepartmentSelect}
          handleEmployeeSelect={handleEmployeeSelect}
          handleClearSelection={handleClearSelection}
        />

        {(selectedEmployee || selectedDepartment) && (
          <div className="bg-indigo-50 border-b border-indigo-200 p-3">
            <div className="flex items-center justify-between">
              <div>
                {selectedEmployee && (
                  <div className="text-indigo-800">
                    <span className="font-medium">
                      Selected: {selectedEmployee.firstName}{" "}
                      {selectedEmployee.lastName}
                    </span>
                    <span className="text-sm ml-2">
                      ({selectedEmployee.department})
                    </span>
                  </div>
                )}
                {selectedDepartment && !selectedEmployee && (
                  <div className="text-indigo-800">
                    <span className="font-medium">
                      Department: {selectedDepartment}
                    </span>
                    {departmentFilterDate && (
                      <span className="text-sm ml-2">
                        • {new Date(departmentFilterDate).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={handleClearSelection}
                className="text-indigo-600 hover:text-indigo-800 p-1"
                title="Clear Selection"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}

        {viewMode === "all" && (
          <div className="bg-white border-b border-gray-200 p-4">
            <div className="max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Date to View All Tasks
                {currentUserRole === "manager" && (
                  <span className="text-xs text-gray-500 ml-2">
                    (from {currentUserDepartment} department)
                  </span>
                )}
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setDateTasksPage(1);
                  setAccumulatedDateTasks([]);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {selectedEmployee ? (
          <>
            <TaskCalendar
              role="employee"
              currentDate={currentDate}
              userTasks={userTasks}
              allTasks={[]}
              selectedEmployee={selectedEmployee}
              selectedDate={selectedDate}
              onDateClick={(dateKey) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const selected = new Date(dateKey);
                selected.setHours(0, 0, 0, 0);
                if (selected > today) {
                  notifyError("Cannot add tasks for future dates");
                  return;
                }

                const existingTasks = userTasks.filter((t) =>
                  t.date?.startsWith(dateKey)
                );

                setSelectedDate(dateKey);

                if (existingTasks.length === 0) {
                  setTaskTitle("");
                  setTaskDescription("");
                  setIsEditMode(false);
                  setEditingTask(null);
                  setShowModal(true);
                }
              }}
            />

            {selectedDate && (
              <TaskList
                title={`Tasks on ${new Date(selectedDate).toLocaleDateString(
                  "en-GB",
                  {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  }
                )}`}
                tasks={userTasks.filter((t) =>
                  t.date?.startsWith(selectedDate)
                )}
                isLoading={userTasksLoading}
                role={currentUserRole || "admin"}
                users={users}
                currentUserId={currentUserId}
                handleTaskEdit={(task) => {
                  const taskDate = task.date.split("T")[0];
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const selected = new Date(taskDate);
                  selected.setHours(0, 0, 0, 0);
                  if (selected > today) {
                    notifyError("Cannot edit tasks for future dates");
                    return;
                  }

                  setIsEditMode(true);
                  setEditingTask(task);
                  setTaskTitle(task.title);
                  setTaskDescription(task.description);
                  setSelectedDate(taskDate);
                  setShowModal(true);
                }}
                handleDeleteTask={(taskId, taskDate) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const selected = new Date(taskDate);
                  selected.setHours(0, 0, 0, 0);
                  if (selected > today) {
                    notifyError("Cannot delete tasks for future dates");
                    return;
                  }

                  if (
                    window.confirm("Are you sure you want to delete this task?")
                  ) {
                    deleteTaskMutation.mutate(taskId);
                  }
                }}
                deleteTaskMutation={deleteTaskMutation}
              />
            )}
          </>
        ) : (
          <TaskList
            {...getTasksToDisplay()}
            role={currentUserRole || "admin"}
            users={users}
            currentUserId={currentUserId}
            handleTaskEdit={handleTaskEdit}
            handleDeleteTask={handleDeleteTask}
            deleteTaskMutation={deleteTaskMutation}
          />
        )}

        <TaskLegend role={currentUserRole || "admin"} />

        <TaskModel
          showModal={showModal}
          setShowModal={setShowModal}
          taskTitle={taskTitle}
          setTaskTitle={setTaskTitle}
          taskDescription={taskDescription}
          setTaskDescription={setTaskDescription}
          handleSubmit={handleTaskSubmit}
          isSubmitting={addTaskMutation.isPending || editTaskMutation.isPending}
          isEditMode={isEditMode}
          selectedEmployee={selectedEmployee}
          role={currentUserRole || "admin"}
          selectedDate={selectedDate}
        />
      </div>
    </div>
  );
};

export default AdminTask;